<template>
  <transition name="fade">
    <div v-if="isOpen" class="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <!-- Overlay -->
      <div class="absolute inset-0 bg-black/80 backdrop-blur-md" @click="close"></div>
      
      <!-- Modal Content -->
      <div 
        class="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 shadow-2xl"
        style="background: rgba(18, 18, 18, 0.95);"
      >
        <!-- Close Button -->
        <button @click="close" class="absolute top-4 right-4 text-white/50 hover:text-white transition-colors">
          <XMarkIcon class="w-6 h-6" />
        </button>

        <div class="p-8">
          <!-- Switch Tabs -->
          <div class="flex justify-center mb-8 bg-white/5 p-1 rounded-xl">
            <button 
              @click="mode = 'login'" 
              :class="['flex-1 py-2 rounded-lg font-bold text-sm transition-all', mode === 'login' ? 'bg-fire-gold text-black' : 'text-gray-400 hover:text-white']"
            >
              Connexion
            </button>
            <button 
              @click="mode = 'register'" 
              :class="['flex-1 py-2 rounded-lg font-bold text-sm transition-all', mode === 'register' ? 'bg-fire-gold text-black' : 'text-gray-400 hover:text-white']"
            >
              Créer un compte
            </button>
          </div>

          <h2 class="text-3xl font-black text-white mb-2 uppercase tracking-tighter">
            {{ mode === 'login' ? 'Bon retour !' : 'Bienvenue !' }}
          </h2>
          <p class="text-gray-400 text-sm mb-6">
            {{ mode === 'login' ? 'Connectez-vous pour finaliser votre commande.' : 'Créez votre compte pour profiter de nos offres.' }}
          </p>

          <!-- Error Alert -->
          <div v-if="auth.error" class="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm font-bold">
            {{ auth.error }}
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div v-if="mode === 'register'" class="space-y-1">
              <label class="text-xs font-black text-fire-gold uppercase tracking-widest ml-1">Nom complet</label>
              <input 
                v-model="form.name"
                type="text" 
                required
                class="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-white focus:border-fire-gold focus:outline-none transition-all"
                placeholder="Jean DuPont"
              />
            </div>

            <div class="space-y-1">
              <label class="text-xs font-black text-fire-gold uppercase tracking-widest ml-1">Email</label>
              <input 
                v-model="form.email"
                type="email" 
                required
                class="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-white focus:border-fire-gold focus:outline-none transition-all"
                placeholder="jean@example.com"
              />
            </div>

            <div class="space-y-1">
              <label class="text-xs font-black text-fire-gold uppercase tracking-widest ml-1">Mot de passe</label>
              <input 
                v-model="form.password"
                type="password" 
                required
                class="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-white focus:border-fire-gold focus:outline-none transition-all"
                placeholder="••••••••"
              />
            </div>

            <button 
              type="submit" 
              class="btn-primary w-full py-4 text-lg font-black uppercase tracking-widest mt-4 flex items-center justify-center gap-2"
              :disabled="auth.loading"
            >
              <span v-if="auth.loading" class="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></span>
              {{ mode === 'login' ? 'Se connecter' : 'Créer mon compte' }}
            </button>
          </form>

          <p class="text-center mt-6 text-xs text-gray-500">
            En continuant, vous acceptez nos <a href="#" class="text-fire-gold underline">Conditions Générales</a>.
          </p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { useAuthStore } from '../stores/auth';
import { XMarkIcon } from '@heroicons/vue/24/solid';

const props = defineProps({
  isOpen: Boolean
});

const emit = defineEmits(['close', 'success']);

const auth = useAuthStore();
const mode = ref('login');
const form = reactive({
  email: '',
  password: '',
  name: ''
});

const close = () => {
  emit('close');
};

const handleSubmit = async () => {
  if (mode.value === 'login') {
    const success = await auth.login({ email: form.email, password: form.password });
    if (success) {
      emit('success');
      close();
    }
  } else {
    const success = await auth.register(form);
    if (success) {
      emit('success');
      close();
    }
  }
};

// Reset form when switching mode
watch(mode, () => {
  auth.error = null;
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.btn-primary {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: black;
  box-shadow: 0 4px 20px rgba(255, 215, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.4);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
